// C:/Repository/everydayFrontend2025/src/hooks/useNotifications.ts
import { useState, useCallback } from 'react';
import {
  getNotifications,
  getNotificationById,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  GetNotificationsParams,
  // Імпортуємо новий інтерфейс GetNotificationsResult
  GetNotificationsResult,
} from '../services/notification.service';
import { Notification } from '../types/notification';

interface NotificationLoadingState {
  notifications: boolean;
  action: boolean;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationDetail, setNotificationDetail] = useState<Notification | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState<NotificationLoadingState>({
    notifications: false,
    action: false,
  });
  const [error, setError] = useState<string | null>(null);

  const handleAction = async <T,>(action: Promise<T>, failureMessage: string): Promise<T | null> => {
    setLoading(prev => ({ ...prev, action: true }));
    setError(null);
    try {
      return await action;
    } catch (err) {
      setError(failureMessage);
      console.error(failureMessage, err);
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const fetchNotifications = useCallback(async (params: GetNotificationsParams = {}) => {
    setLoading(prev => ({ ...prev, notifications: true }));
    setError(null);
    try {
      // Тепер getNotifications повертає { notifications, meta }
      const result: GetNotificationsResult = await getNotifications(params);
      const { notifications: fetchedNotifications, meta } = result;

      setNotifications(fetchedNotifications || []);
      // Використовуємо meta для пагінації
      setTotalPages(meta.limit > 0 ? Math.ceil(meta.total / meta.limit) : 0);
      setCurrentPage(meta.page || 1);
      setTotalCount(meta.total || 0);

    } catch (err) {
      const msg = 'Не вдалося завантажити сповіщення.';
      setError(msg);
      console.error(msg, err);
    } finally {
      setLoading(prev => ({ ...prev, notifications: false }));
    }
  }, []);

  const fetchNotificationDetail = useCallback(async (id: string) => {
    return handleAction(
      getNotificationById(id),
      'Не вдалося завантажити деталі сповіщення.'
    );
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    const result = await handleAction(
      markNotificationAsRead(id),
      'Не вдалося позначити сповіщення як прочитане.'
    );

    if (result !== null) {
      // Оновлюємо локальний стан
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id ? { ...notification, isRead: true } : notification
        )
      );
    }

    return result;
  }, []);

  const markAllAsRead = useCallback(async () => {
    const result = await handleAction(
      markAllNotificationsAsRead(),
      'Не вдалося позначити всі сповіщення як прочитані.'
    );

    if (result !== null) {
      // Оновлюємо локальний стан
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, isRead: true }))
      );
    }

    return result;
  }, []);

  return {
    // Data
    notifications,
    notificationDetail,
    totalPages,
    currentPage,
    totalCount,

    // Loading states
    loading,
    error,

    // Setters
    setNotifications,
    setNotificationDetail,
    setError,

    // Actions
    fetchNotifications,
    fetchNotificationDetail,
    markAsRead,
    markAllAsRead,
  };
};